import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { useBasePath } from '@/lib/paths'
import { DashboardPageProps, PhysicalMailSummary, BusinessRef } from '@/types'
import { Package, ArrowRight, Star, Archive, Lock, Search } from 'lucide-react'

type Props = {
  business: BusinessRef
  physical_mails: PhysicalMailSummary[]
  pagination: PaginationMeta
  service_active: boolean
}

export default function BusinessPhysicalMailsIndex({ business, physical_mails, pagination, service_active }: DashboardPageProps<Props>) {
  const basePath = useBasePath()
  const [search, setSearch] = useState('')

  const filtered = search.trim()
    ? physical_mails.filter(m =>
        m.sender.toLowerCase().includes(search.toLowerCase()) ||
        m.subject?.toLowerCase().includes(search.toLowerCase())
      )
    : physical_mails

  function toggleArchive(mail: PhysicalMailSummary, e: React.MouseEvent) {
    e.stopPropagation()
    const path = `${basePath}/businesses/${business.id}/physical_mails/${mail.id}/archive`
    mail.archived ? router.delete(path) : router.post(path)
  }

  function toggleFavourite(mail: PhysicalMailSummary, e: React.MouseEvent) {
    e.stopPropagation()
    const path = `${basePath}/businesses/${business.id}/physical_mails/${mail.id}/favourite`
    mail.favourited ? router.delete(path) : router.post(path)
  }

  return (
    <DashboardLayout
      title="Physical Mail"
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: business.name, href: `businesses/${business.id}` },
        { label: 'Physical Mail' },
      ]}
    >
      <Head title="Physical Mail" />

      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">Physical Mail</h1>
          </div>
          {!service_active && (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              Upgrade to a Registered Address or Registered Agent service to view mail content.
            </p>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search by sender or subject…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <section className="border rounded-lg">
          {filtered.length > 0 ? (
            <div>
              {filtered.map((mail, i) => (
                <div
                  key={mail.id}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''} ${service_active ? 'cursor-pointer' : ''}`}
                  onClick={() => service_active && router.visit(`${basePath}/businesses/${business.id}/physical_mails/${mail.id}`)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">{mail.sender}</div>
                    <div className="text-xs text-muted-foreground">
                      {mail.subject && <span>{mail.subject} · </span>}
                      {mail.received_on
                        ? `Received: ${mail.received_on}`
                        : new Date(mail.created_at).toLocaleDateString()}
                      {mail.folder_name && ` · ${mail.folder_name}`}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {mail.shredded ? (
                      <Badge className="bg-destructive text-destructive-foreground font-normal text-xs">Shredded</Badge>
                    ) : mail.shred_requested ? (
                      <Badge className="bg-secondary text-secondary-foreground font-normal text-xs">Shred requested</Badge>
                    ) : null}

                    {service_active ? (
                      <>
                        <button
                          onClick={(e) => toggleFavourite(mail, e)}
                          className={`${mail.favourited ? 'text-primary' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
                          title={mail.favourited ? 'Unfavourite' : 'Favourite'}
                        >
                          <Star className="w-3.5 h-3.5" fill={mail.favourited ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          onClick={(e) => toggleArchive(mail, e)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title={mail.archived ? 'Unarchive' : 'Archive'}
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      </>
                    ) : (
                      <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              {search ? 'No mail matches your search' : 'No mail yet'}
            </div>
          )}
        </section>

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
